import { getTypographyMetric, getTypographyMetricOverride } from '../../../../../../../../helpers';
import { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { useCategoryNavigation } from './hooks/useCategoryNavigation';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Flex } from 'front-commons/ds/components/Flex';
import { Icon } from 'front-commons/ds/components/Icon';
import { Skeleton } from 'front-commons/ds/components/Skeleton';
import { Typography } from 'hype-ds';
import { animated } from 'react-spring';
import { useHeader } from '../../../../../../../../context';
import { useCategoriesTransitions } from './hooks/useCategoriesTransitions';

const AnimatedRadioItem = animated(Flex);
const AnimatedCategories = animated(Flex);

export function CategoriesDesktop() {
	const { fontSizes } = useTheme();
	const { open, setOpen, selectedDepartment, setSelectedDepartment, handleNavigateTo } = useCategoryNavigation();

	const {
		categories: { useGetCategoriesPh, posId },
	} = useHeader();

	const { data, isFetching } = useGetCategoriesPh({ posId, enabled: open });

	const { departmentTransitions, categoriesTransition } = useCategoriesTransitions({
		open,
		data,
		selectedDepartment,
	});

	return (
		<DropdownMenu.Root open={open} onOpenChange={setOpen}>
			<DropdownMenu.Trigger asChild aria-expanded={open}>
				<Flex
					as="button"
					display="flex"
					gap="0.25rem"
					padding="0.25rem 0.5rem"
					cursor="pointer"
					borderRadius="8px"
					outline="none"
					raw={{
						'&:hover': { backgroundColor: 'var(--surface-accent-light)' },
						label: { cursor: 'pointer' },
						"[data-header-text='CategoriesDesktop-text-1']": {
							display: 'inline',
							fontSize: getTypographyMetricOverride(fontSizes['16']),
							lineHeight: getTypographyMetricOverride(fontSizes['24']),
						},
					}}
				>
					<Icon color="--text-link" name="menu" />
					<Typography
						textAlign="inherit"
						fontSize={getTypographyMetric(fontSizes['16'])}
						fontWeight="600"
						lineHeight={getTypographyMetric(fontSizes['24'])}
						color="--hympar-blue-700"
						data-header-text="CategoriesDesktop-text-1"
					>
						Categorias
					</Typography>
				</Flex>
			</DropdownMenu.Trigger>

			<DropdownMenu.Portal>
				<DropdownMenu.Content asChild>
					<Flex
						display="block"
						gap="normal"
						overflow="hidden"
						boxShadow="0 2px 4px -2px var(--greys-1000-10), 0 32px 16px -8px var(--greys-1000-10)"
						raw={{
							backgroundColor: 'var(--surface-white)',
							transformOrigin: 'top center',
							"&[data-state='open']": { animation: 'dropdownSlideDown 300ms cubic-bezier(0.87, 0, 0.13, 1)' },
							"&[data-state='closed']": { animation: 'dropdownSlideUp 250ms cubic-bezier(0.87, 0, 0.13, 1)' },
							'*': { transition: 'all 0.3s' },
							'@keyframes dropdownSlideDown': {
								from: { height: '0', opacity: '0', transform: 'scaleY(0.96)' },
								to: {
									height: 'var(--radix-dropdown-menu-content-available-height)',
									opacity: '1',
									transform: 'scaleY(1)',
								},
							},
							'@keyframes dropdownSlideUp': {
								from: {
									height: 'var(--radix-dropdown-menu-content-available-height)',
									opacity: '1',
									transform: 'scaleY(1)',
								},
								to: { height: '0', opacity: '0', transform: 'scaleY(0.96)' },
							},
						}}
						className="header-categories"
					>
						<Flex maxWidth="1264px" width="100%" margin="0 auto" padding="1.5rem 1.25rem 4rem" gap="4.5rem">
							{isFetching && !data?.length && (
								<Flex direction="column" width="100%" gap="1.5rem" padding="1.5rem 1.25rem">
									{Array.from({ length: 5 }).map((_, i) => (
										<Skeleton key={`category-skeleton-${i}`} maxWidth="120px" height="28px" />
									))}
								</Flex>
							)}

							{!!data?.length && (
								<>
									<Flex
										raw={{
											"[data-header-text='CategoriesDesktop-text-2']": {
												display: 'inline',
												fontSize: getTypographyMetricOverride(fontSizes['14']),
												lineHeight: getTypographyMetricOverride(fontSizes['20']),
											},
										}}
										maxWidth="338px"
										width="100%"
										gap="0.5rem"
										direction="column"
									>
										<Typography
											textAlign="inherit"
											fontSize={getTypographyMetric(fontSizes['14'])}
											fontWeight="600"
											lineHeight={getTypographyMetric(fontSizes['20'])}
											color="--greys-700"
											data-header-text="CategoriesDesktop-text-2"
										>
											Departamentos
										</Typography>

										<DropdownMenu.RadioGroup
											asChild
											value={selectedDepartment?.id}
											onValueChange={(departmentId) => setSelectedDepartment(data.find((d) => d.id === departmentId))}
										>
											<Flex display="flex" gap="0.5rem" direction="column">
												{departmentTransitions((style, index) => {
													const department = data[index!];
													const isSelected = selectedDepartment?.id === department.id;

													return (
														<DropdownMenu.RadioItem
															asChild
															key={`department-${department.id}`}
															value={department.id}
															onSelect={(e: Event) => e.preventDefault()}
														>
															<AnimatedRadioItem
																display="flex"
																gap="normal"
																justifyContent="space-between"
																alignItems="center"
																cursor="pointer"
																borderRadius="4px"
																raw={{
																	paddingInline: '0.25rem',
																	'&:hover': { outline: 'none', backgroundColor: 'var(--surface-accent-light)' },
																	"[data-header-text='CategoriesDesktop-text-3']": {
																		display: 'inline',
																		fontSize: getTypographyMetricOverride(fontSizes['20']),
																		lineHeight: getTypographyMetricOverride(fontSizes['28']),
																	},
																}}
																style={style}
															>
																<Typography
																	textAlign="inherit"
																	fontSize={getTypographyMetric(fontSizes['20'])}
																	fontWeight="700"
																	lineHeight={getTypographyMetric(fontSizes['28'])}
																	color={isSelected ? '--greys-900' : '--hympar-blue-700'}
																	whiteSpace="nowrap"
																	data-header-text="CategoriesDesktop-text-3"
																>
																	{department.name.textEllipsis(28)}
																</Typography>

																{isSelected && <Icon name="chevron_right" color="--text-primary" />}
															</AnimatedRadioItem>
														</DropdownMenu.RadioItem>
													);
												})}
											</Flex>
										</DropdownMenu.RadioGroup>
									</Flex>

									{categoriesTransition((style, departmentId) => {
										if (!departmentId || !selectedDepartment) return null;

										return (
											<AnimatedCategories
												display="block"
												gap="normal 3rem"
												maxHeight="540px"
												overflow="auto"
												raw={{
													columns: '4 200px',
													columnFill: 'auto',
													ul: { margin: '0 0 1rem', listStyle: 'none' },
													li: { breakInside: 'avoid' },
												}}
												style={style}
											>
												{selectedDepartment.children?.map((category) => (
													<ul key={`category-${category.id}`}>
														<Flex
															as={Link}
															display="inline"
															gap="normal"
															raw={{
																label: { cursor: 'pointer', '&:hover': { color: 'var(--text-link)' } },
																"[data-header-text='CategoriesDesktop-text-4']": {
																	display: 'inline',
																	fontSize: getTypographyMetricOverride(fontSizes['16']),
																	lineHeight: getTypographyMetricOverride(fontSizes['24']),
																},
															}}
															to={`/departamentos/${departmentId}/categorias/${category.id}`}
															onClick={(e) =>
																handleNavigateTo(e, `/departamentos/${departmentId}/categorias/${category.id}`)
															}
														>
															<Typography
																textAlign="inherit"
																fontSize={getTypographyMetric(fontSizes['16'])}
																fontWeight="600"
																lineHeight={getTypographyMetric(fontSizes['24'])}
																color="--greys-700"
																data-header-text="CategoriesDesktop-text-4"
															>
																{category.name}
															</Typography>
														</Flex>

														{category.children?.map((subcategory) => (
															<li key={subcategory.id}>
																<Flex
																	as={Link}
																	display="inline"
																	gap="normal"
																	raw={{
																		'p, label': {
																			cursor: 'pointer',
																			'&:hover': { color: 'var(--surface-accent-dark)' },
																		},
																		"[data-header-text='CategoriesDesktop-text-5']": {
																			display: 'inline',
																			fontSize: getTypographyMetricOverride(fontSizes['16']),
																			lineHeight: getTypographyMetricOverride(fontSizes['24']),
																		},
																		"[data-header-text='CategoriesDesktop-text-6']": {
																			display: 'inline',
																			fontSize: getTypographyMetricOverride(fontSizes['16']),
																			lineHeight: getTypographyMetricOverride(fontSizes['24']),
																		},
																	}}
																	to={`/departamentos/${departmentId}/categorias/${category.id}?subcategorias=${subcategory.id}`}
																	onClick={(e) =>
																		handleNavigateTo(
																			e,
																			`/departamentos/${departmentId}/categorias/${category.id}?subcategorias=${subcategory.id}`,
																		)
																	}
																>
																	<Typography
																		textAlign="inherit"
																		fontSize={getTypographyMetric(fontSizes['16'])}
																		fontWeight="600"
																		lineHeight={getTypographyMetric(fontSizes['24'])}
																		color="--hympar-blue-700"
																		data-header-text="CategoriesDesktop-text-5"
																	>
																		{subcategory.name}{' '}
																		<Typography
																			textAlign="inherit"
																			fontSize={getTypographyMetric(fontSizes['16'])}
																			fontWeight="400"
																			lineHeight={getTypographyMetric(fontSizes['24'])}
																			as="span"
																			color="--hympar-blue-700"
																			data-header-text="CategoriesDesktop-text-6"
																		>
																			({subcategory.quantity})
																		</Typography>
																	</Typography>
																</Flex>
															</li>
														))}
													</ul>
												))}
											</AnimatedCategories>
										);
									})}
								</>
							)}
						</Flex>
					</Flex>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}
